import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid2';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ITourSchedule } from 'app/shared/model/tour-schedule.model';
import { getEntities as getTourSchedules } from 'app/entities/tour-schedule/tour-schedule.reducer';
import { IPassenger } from 'app/shared/model/passenger.model';
import { getEntities as getPassengers } from 'app/entities/passenger/passenger.reducer';
import { IBooking } from 'app/shared/model/booking.model';
import { getEntity, updateEntity, createEntity, reset } from './booking.reducer';

export const BookingUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tourSchedules = useAppSelector(state => state.tourSchedule.entities);
  const passengers = useAppSelector(state => state.passenger.entities);
  const bookingEntity = useAppSelector(state => state.booking.entity);
  const loading = useAppSelector(state => state.booking.loading);
  const updating = useAppSelector(state => state.booking.updating);
  const updateSuccess = useAppSelector(state => state.booking.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/booking' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({});
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getTourSchedules({}));
    dispatch(getPassengers({}));
  }, []);

  useEffect(() => {
    if (bookingEntity) {
      setFormData({
        ...bookingEntity,
        schedule: bookingEntity?.schedule?.id,
        passenger: bookingEntity?.passenger?.id,
      });
    }
  }, [bookingEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.bookDatetime) {
      newErrors.bookDatetime = 'Booking date is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.noPersons !== undefined && typeof values.noPersons !== 'number') {
      values.noPersons = Number(values.noPersons);
    }
    if (values.noKids !== undefined && typeof values.noKids !== 'number') {
      values.noKids = Number(values.noKids);
    }
    if (values.noPets !== undefined && typeof values.noPets !== 'number') {
      values.noPets = Number(values.noPets);
    }
    if (values.total !== undefined && typeof values.total !== 'number') {
      values.total = Number(values.total);
    }

    values.valid = values.valid === true;

    const entity = {
      ...bookingEntity,
      ...values,
      schedule: tourSchedules.find(it => it.id.toString() === values.schedule?.toString()),
      passenger: passengers.find(it => it.id.toString() === values.passenger?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.booking.home.createOrEditLabel">Create or edit a Booking</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box>
            {!isNew && (
              <TextField
                label={translate('global.field.id')}
                id="booking-id"
                name="id"
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label={translate('jhipsterSampleApplicationApp.booking.bookDatetime')}
              id="booking-bookDatetime"
              name="bookDatetime"
              data-cy="bookDatetime"
              value={formData.bookDatetime || ''}
              onChange={handleChange('bookDatetime')}
              fullWidth
              required
              type="date"
              InputLabelProps={{ shrink: true }}
              error={!!errors.bookDatetime}
              helperText={errors.bookDatetime || ''}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={translate('jhipsterSampleApplicationApp.booking.noPersons')}
                  id="booking-noPersons"
                  name="noPersons"
                  data-cy="noPersons"
                  value={formData.noPersons || ''}
                  onChange={handleChange('noPersons')}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={translate('jhipsterSampleApplicationApp.booking.noKids')}
                  id="booking-noKids"
                  name="noKids"
                  data-cy="noKids"
                  value={formData.noKids || ''}
                  onChange={handleChange('noKids')}
                  fullWidth
                  type="number"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={translate('jhipsterSampleApplicationApp.booking.noPets')}
                  id="booking-noPets"
                  name="noPets"
                  data-cy="noPets"
                  value={formData.noPets || ''}
                  onChange={handleChange('noPets')}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={translate('jhipsterSampleApplicationApp.booking.total')}
                  id="booking-total"
                  name="total"
                  data-cy="total"
                  value={formData.total || ''}
                  onChange={handleChange('total')}
                  fullWidth
                  type="number"
                />
              </Grid>
            </Grid>
            <TextField
              label={translate('jhipsterSampleApplicationApp.booking.paymentType')}
              id="booking-paymentType"
              name="paymentType"
              data-cy="paymentType"
              value={formData.paymentType || ''}
              onChange={handleChange('paymentType')}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.valid || false}
                  onChange={handleChange('valid')}
                  id="booking-valid"
                  name="valid"
                  data-cy="valid"
                />
              }
              label={translate('jhipsterSampleApplicationApp.booking.valid')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.booking.cancelledAt')}
              id="booking-cancelledAt"
              name="cancelledAt"
              data-cy="cancelledAt"
              value={formData.cancelledAt || ''}
              onChange={handleChange('cancelledAt')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.booking.remoteData')}
              id="booking-remoteData"
              name="remoteData"
              data-cy="remoteData"
              value={formData.remoteData || ''}
              onChange={handleChange('remoteData')}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.booking.remoteId')}
              id="booking-remoteId"
              name="remoteId"
              data-cy="remoteId"
              value={formData.remoteId || ''}
              onChange={handleChange('remoteId')}
              fullWidth
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="booking-schedule-label">{translate('jhipsterSampleApplicationApp.booking.schedule')}</InputLabel>
              <Select
                labelId="booking-schedule-label"
                id="booking-schedule"
                name="schedule"
                data-cy="schedule"
                value={formData.schedule || ''}
                label={translate('jhipsterSampleApplicationApp.booking.schedule')}
                onChange={handleSelectChange('schedule')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tourSchedules
                  ? tourSchedules.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="booking-passenger-label">{translate('jhipsterSampleApplicationApp.booking.passenger')}</InputLabel>
              <Select
                labelId="booking-passenger-label"
                id="booking-passenger"
                name="passenger"
                data-cy="passenger"
                value={formData.passenger || ''}
                label={translate('jhipsterSampleApplicationApp.booking.passenger')}
                onChange={handleSelectChange('passenger')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {passengers
                  ? passengers.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.name}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/booking"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BookingUpdate;
